/**
 * Product Recommendation Engine
 *
 * Fetches real products from Supabase for recommendation sections:
 * - Most Recently Viewed
 * - Related Products
 * - Might Also Like
 * - More From Brand
 * - Recent Views
 * - Related To Items Viewed
 * - Browsing History
 * - Compare This Item
 * 
 * NOTE: These functions are used by both Server and Client Components,
 * so React.cache() cannot be used here. The Supabase client is already
 * cached in lib/supabase/client.ts for efficiency.
 */

import { hasRecordedLedLighting } from '@/lib/blog/article-lighting-products'
import type { MembershipProduct } from '@/lib/collection-membership'
import { readSharedCatalog, readThroughDataCache } from '@/lib/shared-catalog-read'
import { createClient } from '@/lib/supabase/client'
import { formatCents } from '@/lib/format-utils'
// FRIDGE-349: every recommendation source selects `images_supabase` and maps
// `images` through the ONE shared selector — mirrored first, on a
// /product-images/… path; the retailer URL only when nothing is mirrored. The
// PDP's "Closest alternatives" and "Top-Rated" rails were the twelve
// m.media-amazon.com occurrences on a fully mirrored PDP (verified in the
// hydrated DOM of two live PDPs, 08/17/2026): four rail cards, each src +
// two srcset entries, all fed by functions in this file that selected `images`
// alone. Rails and grids gate on `isRenderableImageUrl`, never `/^https?:/`.
import {
  getProxiedStoragePath,
  isRenderableImageUrl,
  pickProductImage,
  pickProductImages,
} from '@/lib/image-url'
import { amazonOriginImages } from '@/lib/product-images/amazon-image-licence'
import {
  applySafeProductFilters,
  BANNED_TITLE_KEYWORDS as _BANNED_TITLE_KEYWORDS,
  BANNED_PARTS_BRANDS as _BANNED_PARTS_BRANDS,
  PRODUCT_TYPE_PRICE_FLOOR_CENTS as _PRODUCT_TYPE_PRICE_FLOOR_CENTS,
  FILTER_PRODUCT_TYPES as _FILTER_PRODUCT_TYPES,
  isFilterProductType as _isFilterProductType,
} from '@/lib/product-filters'

// Re-export for backwards compat — many call sites import these from
// lib/recommendations.ts directly. The single source of truth lives in
// lib/product-filters.ts now.
export const BANNED_TITLE_KEYWORDS = _BANNED_TITLE_KEYWORDS
export const BANNED_PARTS_BRANDS = _BANNED_PARTS_BRANDS
export const PRODUCT_TYPE_PRICE_FLOOR_CENTS = _PRODUCT_TYPE_PRICE_FLOOR_CENTS
export const FILTER_PRODUCT_TYPES = _FILTER_PRODUCT_TYPES
export const isFilterProductType = _isFilterProductType
export { applySafeProductFilters }

export interface RecommendedProduct extends MembershipProduct {
  id: string
  brand: string
  model: string
  title: string
  type: string
  images: string[]
  /**
   * Canonical (fridge.com-mirrored) image set when the product has been
   * migrated to Supabase-hosted images. Optional — only populated by sources
   * that select it (e.g. getTrendingInCollection). The canonical image for a
   * product is `images_supabase[0] ?? images[0]` (same precedence as the PDP
   * gallery resolver and the QA image routines; Rule 4 — render from Supabase).
   */
  images_supabase?: string[]
  canonical_url: string
  // From retailer_offers
  price?: number
  originalPrice?: number
  rating?: number
  reviews?: number
  // Product specs for comparison table
  capacity_cuft?: number
  width_in?: number
  height_in?: number
  depth_in?: number
  finish?: string
  specs_json?: {
    freezer_capacity?: number
    number_of_doors?: number
    energy_star?: boolean
    installation?: string
    rating?: number
    reviews?: number
    width_in?: number
    height_in?: number
    depth_in?: number
    [key: string]: any
  }
}


/**
 * De-duplicate by id, order ILLUSTRATED-FIRST then priced, and trim to limit.
 *
 * FRIDGE-398 Step 1 — "image presence becomes a sort key ahead of price on every
 * rail, not just the parts hub". This function is that "every rail": it is the
 * shared selector behind Related, More-From-Brand, Recently-Viewed, Collection
 * recommendations, Popular, Frequently-Bought-Together, Best-Value, Trending,
 * Top-Rated, Editor's-Season-Picks and Featured.
 *
 * It carried the FRIDGE-387 defect one level up. That card measured the shape on
 * the parts hub: 6 of 248 rows had no usable image — 2.4% — and because the sort
 * consulted price and nothing else, all six landed in the top 8 featured cards.
 * 2.4% of rows and 75% of first impressions were both true of the same page.
 * The aggregate was never the answer; render order was.
 *
 * Image presence is the PRIMARY key, matching lib/parts/featured.ts deliberately
 * so the site has one rule rather than two. Price stays the tie-break it always
 * was, so among illustrated cards nothing about the previous behaviour changes.
 *
 * Renderability, not mere presence: `isRenderableImageUrl` is what the PDP rails
 * already use (product-client.tsx `hasRealImage`). A bare `.not('images','is',null)`
 * is a weaker guard than it looks — in Postgres an empty `text[]` is not NULL, so
 * that predicate admits `{}` and dead URLs alike.
 *
 * Image-less candidates are RANKED LAST, never dropped. Silently returning a
 * short rail would hide a catalogue problem; ranking them last surfaces it while
 * still putting the best cards first — the same call lib/parts/featured.ts makes,
 * and the reason it is a sort key rather than a filter.
 */
export function postProcess(items: RecommendedProduct[], limit: number): RecommendedProduct[] {
  const seen = new Set<string>()
  const deduped: RecommendedProduct[] = []
  for (const p of items) {
    if (!p?.id) continue
    if (seen.has(p.id)) continue
    seen.add(p.id)
    deduped.push(p)
  }

  deduped.sort(compareByIllustratedThenPriced)

  return deduped.slice(0, limit)
}

/**
 * True when the card can actually be illustrated — see postProcess.
 *
 * Goes through `pickProductImage`, NOT `images[0]`, so it honours the
 * `images_supabase[0] ?? images[0]` precedence this file documents on the
 * interface. Reading `images[0]` alone would rank a fully-mirrored product as
 * un-illustrated whenever its retailer array happened to be empty — the exact
 * fifty-two-file mistake `lib/image-url.ts` exists to end.
 */
function hasRenderableImage(p: RecommendedProduct): boolean {
  return isRenderableImageUrl(pickProductImage(p))
}

/** The price test postProcess has always applied, extracted so the sort reads. */
function hasPrice(p: RecommendedProduct): boolean {
  return typeof p?.price === 'number' && p.price > 0
}

/**
 * The rail ordering rule — illustrated first, then priced — as a comparator the
 * presentation layer can share.
 *
 * EXPORTED because `postProcess` was not the only copy. Six blocks
 * (bestsellers, since retired by FRIDGE-1501 F7, top-rated, best-value,
 * cool-right-now, more-from-brand, recent-views) each carried a byte-identical
 * price-only comparator and re-sorted what `postProcess` had already ordered —
 * so fixing the selector alone would have left them free to re-scramble the
 * result and put un-illustrated cards back at the front. Six copies of one rule
 * is also how the rule drifts: this is now the only place it is written.
 *
 * Same contract as postProcess: ranks, never drops.
 */
export function compareByIllustratedThenPriced(
  a: RecommendedProduct,
  b: RecommendedProduct
): number {
  return (
    (hasRenderableImage(b) ? 1 : 0) - (hasRenderableImage(a) ? 1 : 0) ||
    (hasPrice(b) ? 1 : 0) - (hasPrice(a) ? 1 : 0)
  )
}

// Soft-logs recommendation errors without triggering Next.js dev overlay
function logRecommendationError(context: string, error: any) {
  // Only log in development - recommendations are optional and shouldn't pollute logs
  if (process.env.NODE_ENV === 'development') {
    const message = (error as any)?.message || String(error)
    console.warn(`[Recs] ${context}:`, message)
  }
}

// Centralized allow/deny rules now live in lib/product-filters.ts. The
// re-exports above keep historical importers (lib/recommendations.ts callers)
// working unchanged. New code should import directly from
// '@/lib/product-filters' and use applySafeProductFilters as the chokepoint.

/**
 * @deprecated Use `applySafeProductFilters` from lib/product-filters.ts.
 * This wrapper exists for backwards compat with callers that imported
 * `applyApprovedFilters` from this module.
 */
export function applyApprovedFilters(query: any) {
  return applySafeProductFilters(query)
}

/**
 * Get related products (same type, different brand)
 */
export async function getRelatedProducts(
  productType: string,
  currentProductId: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .eq('status', 'active')
    .eq('type', productType)
    .neq('id', currentProductId)

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const q = applySafeProductFilters(base, { constrainTypes: false })
  const { data: products, error } = await q.limit(limit)

  // Only log if there's a real error with a message
  if (error?.message) {
    logRecommendationError('related products', error)
    return []
  }

  if (!products || products.length === 0) {
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

/**
 * Get products from the same brand
 */
export async function getMoreFromBrand(
  brand: string,
  currentProductId: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .eq('brand', brand)
    .neq('id', currentProductId)
    .eq('status', 'active')

  const { data: products, error } = await applyApprovedFilters(base).limit(limit)

  if (error || !products) {
    // Silently handle - brand products are optional
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

/**
 * Get recently viewed products (for now, random products - will be replaced with actual user history)
 */
export async function getRecentlyViewed(
  limit: number = 6
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  // TODO: Replace with actual user viewing history from cookies/localStorage
  // For now, get random products with variety
  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .eq('status', 'active')

  const { data: products, error } = await applyApprovedFilters(base).limit(limit * 10) // Fetch 10x for better type diversity

  if (error || !products) {
    // Silently handle - recently viewed is optional
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  
  // Ensure type diversity: prioritize full-size products, limit compact to 1-2 max
  const fullSize: RecommendedProduct[] = []
  const compact: RecommendedProduct[] = []
  
  recs.forEach(rec => {
    if (rec.type === 'compact' || rec.type === 'mini_fridge') {
      compact.push(rec)
    } else {
      fullSize.push(rec)
    }
  })
  
  // Combine: prioritize full-size, allow max 1 compact for every 5 products requested
  // Note: No random shuffle - postProcess handles price-based sorting
  const maxCompact = Math.max(1, Math.floor(limit / 5))
  const diverse = [...fullSize.slice(0, limit), ...compact.slice(0, maxCompact)]
  
  return postProcess(diverse, limit)
}

/**
 * Get products by a list of IDs — used for Recently Viewed tracking
 * Preserves the order of the IDs passed in.
 */
export async function getProductsByIds(
  ids: string[],
  limit: number = 18
): Promise<RecommendedProduct[]> {
  if (!ids || ids.length === 0) return []
  const supabase = createClient()

  const { data: products, error } = await supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .in('id', ids.slice(0, limit))
    .eq('status', 'active')

  if (error || !products) {
    logRecommendationError('getProductsByIds', error)
    return []
  }

  // Preserve the original order from the IDs array (most recent first)
  const byId = new Map(products.map((p: any) => [p.id, p]))
  const ordered = ids
    .map(id => byId.get(id))
    .filter(Boolean)
    .map((p: any) => ({
      id: p.id,
      brand: p.brand,
      model: p.model,
      title: p.title,
      type: p.type,
      images: pickProductImages(p),
      canonical_url: p.canonical_url || '',
      capacity_cuft: p.capacity_cuft,
      finish: p.finish,
      specs_json: p.specs_json,
      price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
      originalPrice: undefined,
      rating: p.specs_json?.rating,
      reviews: p.specs_json?.reviews,
    }))

  return ordered.slice(0, limit)
}

/**
 * Get products for collection page recommendations
 */
export async function getCollectionRecommendations(
  collectionType: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      width_in,
      height_in,
      depth_in,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .eq('type', collectionType)
    .eq('status', 'active')

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const q = applySafeProductFilters(base, { constrainTypes: false })
  const { data: products, error} = await q.limit(limit)

  if (error || !products) {
    // Silently handle - collection recommendations are optional
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    width_in: p.width_in,
    height_in: p.height_in,
    depth_in: p.depth_in,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

/**
 * Get popular products (most viewed/highest rated - for now, random)
 */
export async function getPopularProducts(
  limit: number = 4
): Promise<RecommendedProduct[]> {
  // TODO: Add view count tracking and sort by popularity
  // For now, fetch more and randomize to ensure variety
  const fetchLimit = limit * 10 // Fetch 10x for better type diversity
  // FRIDGE-1241: the read has no ORDER BY, so every caller with the same limit
  // gets the same rows. It ran on every article, local, rebate, brand and
  // product render, uncached: 13.2% of all database statement time from
  // 10/08/2026 to 10/17/2026. One shared read per limit
  // (lib/shared-catalog-read.ts).
  const products = await readSharedCatalog('popular-products-v1', String(fetchLimit), async () => {
    const supabase = createClient()
    const base = supabase
      .from('products')
      .select(`
        id,
        brand,
        model,
        title,
        type,
        images,
        images_supabase,
        canonical_url,
        capacity_cuft,
        finish,
        specs_json,
        lowest_price_cents
      `)
      .eq('status', 'active')
    const { data, error } = await applyApprovedFilters(base).limit(fetchLimit)
    if (error || !data) throw new Error(`popular products read failed: ${error?.message ?? 'no data'}`)
    return data as any[]
  }).catch(() => null)

  if (!products) {
    // Silently handle - popular products are optional
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  
  // Ensure type diversity: prioritize full-size products, limit compact to 1-2 max
  const fullSize: RecommendedProduct[] = []
  const compact: RecommendedProduct[] = []
  
  recs.forEach(rec => {
    if (rec.type === 'compact' || rec.type === 'mini_fridge') {
      compact.push(rec)
    } else {
      fullSize.push(rec)
    }
  })
  
  // Combine: prioritize full-size, allow max 1 compact for every 5 products requested
  // Note: No random shuffle - postProcess handles price-based sorting
  const maxCompact = Math.max(1, Math.floor(limit / 5))
  const diverse = [...fullSize.slice(0, limit), ...compact.slice(0, maxCompact)]
  
  return postProcess(diverse, limit)
}

/**
 * Get frequently bought together products (cross-category recommendations)
 * For refrigerators, recommend ice makers, wine coolers, mini fridges, etc.
 */
export async function getFrequentlyBoughtTogether(
  productType: string,
  currentProductId: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  // Map product types to complementary types
  const complementaryTypes: Record<string, string[]> = {
    'french_door': ['ice_maker', 'wine_cooler', 'mini_fridge', 'beverage_center'],
    'side_by_side': ['ice_maker', 'wine_cooler', 'mini_fridge', 'beverage_center'],
    'top_freezer': ['ice_maker', 'mini_fridge', 'beverage_center'],
    'bottom_freezer': ['ice_maker', 'wine_cooler', 'mini_fridge'],
    'mini_fridge': ['wine_cooler', 'beverage_center', 'ice_maker'],
    'upright_freezer': ['chest_freezer', 'ice_maker'],
    'chest_freezer': ['upright_freezer', 'ice_maker'],
    'wine_cooler': ['beverage_center', 'mini_fridge', 'kegerator'],
    'beverage_center': ['wine_cooler', 'mini_fridge', 'kegerator'],
    'ice_maker': ['mini_fridge', 'beverage_center', 'wine_cooler'],
  }

  const targetTypes = complementaryTypes[productType] || ['mini_fridge', 'ice_maker', 'wine_cooler']

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .in('type', targetTypes)
    .eq('status', 'active')
    .neq('id', currentProductId)

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const q = applySafeProductFilters(base, { constrainTypes: false })
  const { data: products, error } = await q.limit(limit * 2)

  // Only log if there's a real error with a message
  if (error?.message) {
    logRecommendationError('frequently bought together', error)
    return []
  }

  if (!products || products.length === 0) {
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

/**
 * Format price for display.
 *
 * Canonical display rule (owner ruling 07/25/2026): whole dollars drop ".00",
 * real cents render exactly. Shared implementation in lib/format-utils.
 */
export function formatPrice(cents: number | undefined): string {
  if (!cents) return 'Price unavailable'
  return formatCents(cents)
}

/**
 * Get product image URL (first image or placeholder)
 */
export function getProductImage(images: string[] | undefined): string {
  if (!images || images.length === 0) {
    return '/placeholder.svg'
  }
  return images[0]
}

/**
 * SMART FALLBACK WRAPPERS - These ensure sections never disappear
 * Returns { products, title, fallbackLevel } for dynamic UI
 */

export type FallbackResult = {
  products: RecommendedProduct[]
  title: string
  fallbackLevel: 'primary' | 'fallback1' | 'fallback2' | 'fallback3'
}

/**
 * Frequently Bought Together with Smart Fallbacks
 * Primary: Cross-category products → Fallback1: Same category, different brand → Fallback2: Related types → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel, picks first with enough results
 */
export async function getFrequentlyBoughtTogetherWithFallback(
  productType: string,
  currentProductId: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2, fallback3] = await Promise.all([
    getFrequentlyBoughtTogether(productType, currentProductId, limit),
    getRelatedProducts(productType, currentProductId, limit),
    getRelatedCollections(productType, limit),
    getPopularProducts(limit)
  ])

  // Pick first source with enough results
  if (primary.length >= limit) {
    return { products: primary, title: 'Complete Your Kitchen', fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: 'You May Also Like', fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2, title: 'Similar Products', fallbackLevel: 'fallback2' }
  }
  return { products: fallback3, title: 'Popular on Fridge.com', fallbackLevel: 'fallback3' }
}


/**
 * Get best value products (lowest price)
 */
export async function getBestValueProducts(
  collectionType: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  let query = supabase
    .from('products')
    .select(`
      id, title, brand, model, type, images, images_supabase,
      canonical_url,
      lowest_price_cents,
      specs_json
    `)
    .eq('type', collectionType)
    .eq('status', 'active')
    .order('lowest_price_cents', { ascending: true, nullsFirst: false })
    .limit(limit * 4) // Fetch more for filtering (price-floor drops some)

  // Apply centralized filters
  query = applyApprovedFilters(query)

  // Per-type price floor at the SQL level — getBestValueProducts sorts
  // ascending by price, so without the floor the result is dominated by
  // mis-typed parts. Verified May 9, 2026.
  const floor = PRODUCT_TYPE_PRICE_FLOOR_CENTS[collectionType]
  if (typeof floor === 'number') {
    query = query.gte('lowest_price_cents', floor)
  }

  const { data, error } = await query

  if (error) {
    logRecommendationError('getBestValueProducts', error)
    return []
  }

  // Transform and limit
  return postProcess(data?.map((p: any) => ({
    id: p.id,
    title: p.title,
    brand: p.brand,
    model: p.model,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews
  })) || [], limit)
}

/**
 * Get best value products with fallback
 * OPTIMIZED: Fetches both sources in parallel
 */
export async function getBestValueProductsWithFallback(
  collectionType: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch both sources in parallel
  const [primary, fallback] = await Promise.all([
    getBestValueProducts(collectionType, limit),
    getPopularProducts(limit)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: `Best Value ${collectionName}`, fallbackLevel: 'primary' }
  }
  return { products: fallback, title: 'Budget Friendly Picks', fallbackLevel: 'fallback1' }
}

/**
 * Closest alternatives — the PDP "Still deciding?" rail (Slot B, 2026-07-08
 * reorder). Same type, same price band (±20% of the anchor when known),
 * ranked so form-factor matches surface first, then higher-rated, then
 * closest on price. Excludes the current product AND the models already shown
 * in the on-page comparison table (so a shopper never sees the same alt twice).
 *
 * Scoping is enforced in SQL for the reliable filters (type / status / price
 * band) and applied as a RANK — not a hard filter — for form_factor, so a
 * sparse form_factor never empties the rail (the caller's min-items gate
 * decides whether to render at all).
 */
export async function getClosestAlternatives(opts: {
  excludeProductId: string
  type: string | null
  formFactor?: string[] | null
  priceCents?: number | null
  /** Product ids already surfaced in the comparison table — de-duped out. */
  excludeProductIds?: string[]
  limit?: number
  /**
   * FRIDGE-1524: the product page reads this rail on the server and needs to
   * tell a failed read (the browser reads the rail again) from a product with
   * no close alternatives (nothing to read). With this set, a failed read
   * throws instead of answering with no rows.
   */
  throwOnError?: boolean
}): Promise<RecommendedProduct[]> {
  const { excludeProductId, type, formFactor, priceCents, excludeProductIds = [], limit = 12, throwOnError = false } = opts
  if (!type) return []
  const supabase = createClient()

  let query = supabase
    .from('products')
    .select(`
      id, title, brand, model, type, images, images_supabase,
      canonical_url,
      lowest_price_cents,
      finish,
      capacity_cuft,
      form_factor,
      specs_json
    `)
    .eq('type', type)
    .eq('status', 'active')
    .neq('id', excludeProductId)
    .limit(limit * 5) // over-fetch: rank + de-dupe trims below

  // Centralized allow/deny rules (banned parts brands, junk keywords, …).
  query = applyApprovedFilters(query)

  // Price band ±20% — only when we have a trustworthy anchor price. Skipping
  // the band on a null price is deliberate (a spec-only product still gets a
  // rail; a bad band would empty it).
  if (typeof priceCents === 'number' && priceCents > 0) {
    query = query
      .gte('lowest_price_cents', Math.round(priceCents * 0.8))
      .lte('lowest_price_cents', Math.round(priceCents * 1.2))
  }

  const { data, error } = await query
  if (error) {
    if (throwOnError) throw new Error(`getClosestAlternatives: ${(error as { message?: string }).message ?? String(error)}`)
    logRecommendationError('getClosestAlternatives', error)
    return []
  }

  const exclude = new Set<string>([excludeProductId, ...excludeProductIds])
  const wantedFf = new Set((formFactor ?? []).filter(Boolean))

  const scored = (data ?? [])
    .filter((p: any) => p?.id && !exclude.has(p.id))
    .map((p: any) => {
      const pff: string[] = Array.isArray(p.form_factor) ? p.form_factor : []
      const ffOverlap = wantedFf.size > 0 && pff.some((f) => wantedFf.has(f)) ? 1 : 0
      const rating = typeof p.specs_json?.rating === 'number' ? p.specs_json.rating : 0
      const priceGap =
        typeof priceCents === 'number' && priceCents > 0 && typeof p.lowest_price_cents === 'number'
          ? Math.abs(p.lowest_price_cents - priceCents)
          : Number.MAX_SAFE_INTEGER
      return { p, ffOverlap, rating, priceGap }
    })
    .sort((a, b) => b.ffOverlap - a.ffOverlap || b.rating - a.rating || a.priceGap - b.priceGap)

  // Preserve the rank (postProcess re-sorts by price, which we don't want).
  const seen = new Set<string>()
  const out: RecommendedProduct[] = []
  for (const { p } of scored) {
    if (seen.has(p.id)) continue
    seen.add(p.id)
    out.push({
      id: p.id,
      title: p.title,
      brand: p.brand,
      model: p.model,
      type: p.type,
      images: pickProductImages(p),
      canonical_url: p.canonical_url || '',
      price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
      finish: p.finish,
      capacity_cuft: p.capacity_cuft,
      rating: p.specs_json?.rating,
      reviews: p.specs_json?.reviews,
    })
    if (out.length >= limit) break
  }
  return out
}

/**
 * Get more from brand with fallback
 * OPTIMIZED: Fetches brand products and fallback in parallel
 */
export async function getMoreFromBrandWithFallback(
  collectionType: string,
  brandName: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  if (!brandName) {
    const popular = await getPopularProducts(limit)
    return { products: popular, title: 'Popular Brands', fallbackLevel: 'fallback1' }
  }

  const supabase = createClient()
  
  // Fetch brand products and fallback in parallel
  const [brandResult, fallback] = await Promise.all([
    supabase
      .from('products')
      .select('id, title, brand, model, type, images, images_supabase, canonical_url, lowest_price_cents, specs_json')
      .eq('type', collectionType)
      .ilike('brand', brandName)
      .eq('status', 'active')
      .limit(limit * 2),
    getPopularProducts(limit)
  ])

  const primary = postProcess(brandResult.data?.map((p: any) => ({
    id: p.id,
    title: p.title,
    brand: p.brand,
    model: p.model,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews
  })) || [], limit)

  if (primary.length >= limit) {
    return { products: primary, title: `More from ${brandName}`, fallbackLevel: 'primary' }
  }
  return { products: fallback, title: 'Popular Alternatives', fallbackLevel: 'fallback1' }
}

/**
 * Trending Products with Smart Fallbacks
 * Primary: Trending in collection → Fallback1: Same type, sorted by price → Fallback2: Related types → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel
 */
export async function getTrendingWithFallback(
  collectionType: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2, fallback3] = await Promise.all([
    getTrendingInCollection(collectionType, limit),
    getCollectionRecommendations(collectionType, limit),
    getRelatedCollections(collectionType, limit),
    getPopularProducts(limit)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: `Hot Right Now in ${collectionName}`, fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: `Popular ${collectionName}`, fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2, title: 'You May Also Like', fallbackLevel: 'fallback2' }
  }
  return {
    products: fallback3,
    title: 'Popular on Fridge.com',
    fallbackLevel: 'fallback3'
  }
}

/**
 * Related Collections with Smart Fallbacks
 * Primary: Related types → Fallback1: Same type, different brand → Fallback2: Any refrigeration → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel
 */
export async function getRelatedCollectionsWithFallback(
  collectionType: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2] = await Promise.all([
    getRelatedCollections(collectionType, limit),
    getCollectionRecommendations(collectionType, limit),
    getPopularProducts(limit * 2)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: 'You May Also Need', fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: `More ${collectionName}`, fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2.slice(0, limit), title: 'Browse All Categories', fallbackLevel: 'fallback2' }
  }
  return { products: fallback2, title: 'Popular on Fridge.com', fallbackLevel: 'fallback3' }
}

/**
 * Featured Products with Smart Fallbacks
 * Primary: Premium (rating ≥4.5, price ≥$1000) → Fallback1: High-rated (≥4.0) → Fallback2: Same type → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel
 */
export async function getFeaturedProductsWithFallback(
  collectionType: string,
  collectionName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2, fallback3] = await Promise.all([
    getFeaturedProducts(collectionType, limit),
    getTrendingInCollection(collectionType, limit),
    getCollectionRecommendations(collectionType, limit),
    getPopularProducts(limit)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: 'Premium Picks', fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: `Top-Rated ${collectionName}`, fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2, title: `More ${collectionName}`, fallbackLevel: 'fallback2' }
  }
  return { products: fallback3, title: 'Popular on Fridge.com', fallbackLevel: 'fallback3' }
}

/**
 * PRODUCT PAGE FALLBACK WRAPPERS
 */

/**
 * Customers Also Viewed with Smart Fallbacks
 * Primary: Same type, different product → Fallback1: Related types → Fallback2: Same brand → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel
 */
export async function getCustomersAlsoViewedWithFallback(
  productType: string,
  productId: string,
  productBrand: string,
  productTypeName: string,
  limit: number = 6
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2, fallback3] = await Promise.all([
    getRelatedProducts(productType, productId, limit),
    getRelatedCollections(productType, limit),
    getMoreFromBrand(productBrand, productId, limit),
    getPopularProducts(limit)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: 'Similar Products', fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: 'Compare These', fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2, title: `More From ${productBrand}`, fallbackLevel: 'fallback2' }
  }
  return {
    products: fallback3,
    title: 'Popular on Fridge.com',
    fallbackLevel: 'fallback3'
  }
}

/**
 * After Buying This with Smart Fallbacks
 * Primary: Related types (upgrade path) → Fallback1: Same type, higher price → Fallback2: Same type, different brand → Fallback3: Popular
 * OPTIMIZED: Fetches all potential sources in parallel
 */
export async function getAfterBuyingWithFallback(
  productType: string,
  productId: string,
  productTypeName: string,
  limit: number = 4
): Promise<FallbackResult> {
  // Fetch ALL potential sources in parallel
  const [primary, fallback1, fallback2, fallback3] = await Promise.all([
    getRelatedCollections(productType, limit),
    getRelatedProducts(productType, productId, limit),
    getTrendingInCollection(productType, limit),
    getPopularProducts(limit)
  ])

  if (primary.length >= limit) {
    return { products: primary, title: 'Next Purchase Ideas', fallbackLevel: 'primary' }
  }
  if (fallback1.length >= limit) {
    return { products: fallback1, title: 'Upgrade Your Kitchen', fallbackLevel: 'fallback1' }
  }
  if (fallback2.length >= limit) {
    return { products: fallback2, title: `Popular ${productTypeName}`, fallbackLevel: 'fallback2' }
  }
  return { products: fallback3, title: 'Popular on Fridge.com', fallbackLevel: 'fallback3' }
}

/**
 * FRIDGE-1134 — the popularity proxy, and what it does when there is nothing
 * left to proxy.
 *
 * `specs_json.rating` and `specs_json.reviews` are NULL on EVERY active,
 * pdp_eligible product row — 7,534 of 7,534, counted 09/19/2026 — so the old
 * `(rating || 0) * (reviews || 0)` comparator scored every product 0 and
 * returned 0 for every pair. `Array.prototype.sort` is stable, so a comparator
 * that is 0 everywhere is a no-op: the rail's order was simply the order the
 * database handed back, and the database had been asked for `.limit()` with no
 * ORDER BY. A "trending" rail ranked by nothing is the defect, independent of
 * anything to do with link text.
 *
 * Tier 0 — a real review signal (rating x reviews), high to low. Kept first so
 *          the rail upgrades itself the moment review data lands.
 * Tier 1 — `products.popularity_score` (present on 3,714 of the same 7,534
 *          rows, range 0-19), high to low.
 * Tier 2 — neither. Ranked LAST rather than silently tied with everything
 *          else, so an unranked product can never outrank a ranked one.
 *
 * Scores from different tiers are never weighed against each other: the tier
 * is the first key, so a 0-19 popularity score cannot be compared with a
 * rating x reviews product in the thousands.
 *
 * Ties inside a tier fall through to `id`, the primary key, so the ordering is
 * TOTAL — identical on every render, every deploy and every replica.
 */
export type TrendingRankable = {
  id?: string | null
  specs_json?: { rating?: number | null; reviews?: number | null } | null
  popularity_score?: number | null
}

export function trendingRank(p: TrendingRankable | null | undefined): { tier: number; score: number } {
  const specs = p?.specs_json ?? null
  const rating = typeof specs?.rating === 'number' ? specs.rating : 0
  const reviews = typeof specs?.reviews === 'number' ? specs.reviews : 0
  const reviewScore = rating * reviews
  if (reviewScore > 0) return { tier: 0, score: reviewScore }
  const popularity = p?.popularity_score
  if (typeof popularity === 'number') return { tier: 1, score: popularity }
  return { tier: 2, score: 0 }
}

/** Total order: tier first, then score high-to-low, then the primary key. */
export function compareTrending(a: TrendingRankable, b: TrendingRankable): number {
  const ra = trendingRank(a)
  const rb = trendingRank(b)
  if (ra.tier !== rb.tier) return ra.tier - rb.tier
  if (ra.score !== rb.score) return rb.score - ra.score
  return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
}

/**
 * FRIDGE-1323 — the trending pool is read once and shared.
 *
 * Every article page asks for several rails, and each rail used to issue its
 * own read of the same pool: up to 240 full product rows (specs, images) per
 * call. Measured 10/02/2026: 1,187 server renders of /blogs/news/[slug] in 30
 * minutes kept 9 to 14 of these reads in flight, each over a second, and the
 * database could no longer answer the collection read inside the anonymous
 * role's 3 s statement timeout. Two production builds and six CI builds failed
 * on that timeout the same day.
 *
 * The rows for one pool are identical for every caller, so callers now share
 * them: concurrent callers await one in-flight read, and a successful read is
 * reused for TRENDING_POOL_TTL_MS. A failed read is never stored. Callers
 * must treat the rows as read-only (the function below only filters, copies
 * and maps them).
 *
 * Cancellation is shared too. A caller that aborts stops waiting at once; the
 * request itself is cancelled only when every caller waiting on it has
 * aborted, so one caller's spent budget cannot fail the read for another.
 * A caller without a signal keeps the request alive to completion.
 */
const TRENDING_POOL_TTL_MS = 5 * 60 * 1000
const TRENDING_POOL_MAX_KEYS = 64

/**
 * FRIDGE-1615 — the pool is also shared ACROSS instances, through the Next
 * data cache. The map above shares one read per instance, but a crawler burst
 * lands on many fresh instances at once and each read the pool again: on
 * 10/15/2026 at 13:24 UTC Postgres cancelled 326 of these reads in two minutes
 * and 92 article renders hit the 60 s limit (504). One revalidation a day,
 * the same window as the article, product and part pages that render it, so
 * the cache never shortens a page's own ISR window (the lowest revalidate in a
 * render wins). The `products` tag lets an on-demand product change refresh
 * it. A failed read throws inside the cached function, so a failure is never
 * stored. In a browser bundle `unstable_cache` calls the read directly; in
 * tests and scripts there is no data cache, so the read runs directly too.
 * FRIDGE-1241 moved the wrapper to lib/shared-catalog-read.ts, unchanged, so
 * the other shared catalog reads use the same one; the per-instance share
 * above stays this pool's own (its cancellation rules are FRIDGE-1323's).
 */
function readSharedTrendingPool(key: string, read: () => Promise<any[]>): Promise<any[]> {
  return readThroughDataCache('trending-pool-v1', key, read)
}
type TrendingPool = {
  at: number
  rows: Promise<any[] | null>
  cancel: AbortController
  /** Callers still waiting that could abort. */
  waiting: number
  /** A caller without a signal is waiting: never cancel. */
  pinned: boolean
  settled: boolean
}
const trendingPools = new Map<string, TrendingPool>()

function readTrendingPool(
  key: string,
  read: (cancel: AbortSignal) => Promise<any[] | null>,
  caller?: AbortSignal,
): Promise<any[] | null> {
  const now = Date.now()
  let pool = trendingPools.get(key)
  if (!pool || now - pool.at >= TRENDING_POOL_TTL_MS) {
    const cancel = new AbortController()
    const created: TrendingPool = { at: now, rows: read(cancel.signal).catch(() => null), cancel, waiting: 0, pinned: false, settled: false }
    // A failed, cancelled or empty-handed read must not be served to the next caller.
    void created.rows.then((value) => {
      created.settled = true
      if (!value && trendingPools.get(key) === created) trendingPools.delete(key)
    })
    trendingPools.set(key, created)
    if (trendingPools.size > TRENDING_POOL_MAX_KEYS) {
      const oldest = trendingPools.keys().next().value
      if (oldest !== undefined && oldest !== key) trendingPools.delete(oldest)
    }
    pool = created
  }
  const joined = pool
  if (!caller) {
    joined.pinned = true
    return joined.rows
  }
  joined.waiting += 1
  return new Promise<any[] | null>((resolve, reject) => {
    const onAbort = () => {
      joined.waiting -= 1
      if (!joined.settled && !joined.pinned && joined.waiting === 0) {
        if (trendingPools.get(key) === joined) trendingPools.delete(key)
        joined.cancel.abort(caller.reason)
      }
      reject(caller.reason)
    }
    caller.addEventListener('abort', onAbort, { once: true })
    joined.rows.then(resolve, reject).finally(() => caller.removeEventListener('abort', onAbort))
  })
}

/** Test seam: forget every shared trending pool. */
export function resetTrendingPoolsForTests(): void {
  trendingPools.clear()
}

/**
 * Get trending products in a collection.
 *
 * Ranked by `compareTrending` above, and ordered in the database on the same
 * key before `.limit()` so the pool is a real top-N rather than a heap slice.
 * TODO: replace the proxy with real view-count data once it exists.
 */
export async function getTrendingInCollection(
  collectionType: string,
  limit: number = 4,
  options: { signal?: AbortSignal; randomize?: boolean; offset?: number; titleMatch?: string[]; requireStock?: boolean; requireLedLighting?: boolean; category?: 'refrigerator' | 'freezer' } = {}
): Promise<RecommendedProduct[]> {
  options.signal?.throwIfAborted()
  // titleMatch (FRIDGE-578 follow-up, owner 08/27/2026): OR-of-ilike terms so a
  // comparison page can carousel a sub-family the type column cannot express
  // (nugget vs cube ice makers). Code-constant terms only — never user input.
  const { randomize = false, offset = 0, titleMatch, requireStock = false, requireLedLighting = false, category } = options
  if (isFilterProductType(collectionType)) {
    return []
  }

  const supabase = createClient()

  // For now, sort by rating * reviews as a proxy for popularity
  // TODO: Replace with actual PostHog view count tracking
  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      category,
      form_factor,
      tags,
      features,
      width_in,
      color,
      is_commercial,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents,
      popularity_score
      ${requireStock ? ", retailer_offers!inner(in_stock,status)" : ""}
    `)
    .eq('status', 'active')
  // Lighting is a family feature, not a product type: include door styles.
  const typed = category
    ? base.eq('category', category)
    : requireLedLighting
    ? base.eq('category', ['freezer', 'chest-freezer', 'upright-freezer', 'compact-freezer', 'deep-freezer'].includes(collectionType) ? 'freezer' : 'refrigerator')
    : base.eq('type', collectionType)

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const scoped = titleMatch && titleMatch.length > 0
    ? typed.or(titleMatch.map((t) => `title.ilike.%${t}%`).join(','))
    : typed
  const stockScoped = requireStock
    ? scoped.eq('retailer_offers.in_stock', true).eq('retailer_offers.status', 'active')
    : scoped
  const q = applySafeProductFilters(stockScoped, { constrainTypes: false })
  // Fetch a bigger pool when shuffling/skipping so we still hit `limit`
  // after offset + filtering. The LED-lighting gate filters AFTER the read, and
  // only 181 of 2,372 in-stock refrigerators record LED lighting — the 24th
  // sits at popularity rank 150 — so a 3× window could never fill a lighting
  // article's rails (FRIDGE-1187 measured 15 and 7). Lighting reads 10×.
  const windowFactor = requireLedLighting ? 10 : 3
  const fetchLimit = Math.max(limit * windowFactor, (offset + limit) * windowFactor)
  // FRIDGE-1134 — `.limit()` with no ORDER BY is not a "top N". Postgres is
  // free to return whichever rows the scan reaches first, and it returns the
  // SAME arbitrary rows on every render, so the pool this rail drew from was a
  // stable heap slice rather than a ranking — which is why a handful of
  // products became the destination of most of the article rails' links.
  // Rank in the database on the same key the in-memory comparator uses, and
  // close with the primary key so the slice is a TOTAL order: reproducible
  // across renders, deploys and replicas.
  const rankedQuery = q
    .order('popularity_score', { ascending: false, nullsFirst: false })
    .order('lowest_price_cents', { ascending: true, nullsFirst: false })
    .order('id', { ascending: true })
    .limit(fetchLimit)
  // FRIDGE-1323 — one read per pool, shared. The key is everything that shapes
  // the SQL; `limit`, `offset` and `randomize` only slice the pool afterwards.
  const poolKey = JSON.stringify([collectionType, category ?? null, requireStock, requireLedLighting, titleMatch ?? null, fetchLimit])
  const rawProducts = await readTrendingPool(
    poolKey,
    async (cancel) => {
      try {
        return await readSharedTrendingPool(poolKey, async () => {
          // Only a read started by a caller that can abort carries a signal, as before.
          const { data, error } = await (options.signal ? rankedQuery.abortSignal(cancel) : rankedQuery)
          if (error || !data) throw new Error(`trending pool read failed: ${error?.message ?? 'no data'}`)
          return data as any[]
        })
      } catch {
        return null
      }
    },
    options.signal,
  )
  options.signal?.throwIfAborted()

  if (!rawProducts) {
    // Silently handle - trending products are optional
    return []
  }

  const products = rawProducts.filter((p: any) => !isFilterProductType(p.type || '') && (!requireLedLighting || hasRecordedLedLighting(p)))

  let pool: any[]
  if (randomize) {
    // Fisher–Yates shuffle so the same collection rendered twice in two
    // carousels yields different items (or at least different order).
    pool = [...products]
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[pool[i], pool[j]] = [pool[j], pool[i]]
    }
  } else {
    // FRIDGE-1134 — the tiered, total ordering. See compareTrending above for
    // why `rating * reviews` alone could not order anything.
    pool = [...products].sort(compareTrending)
  }

  const recs = pool.slice(offset, offset + limit).map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    category: p.category,
    form_factor: p.form_factor,
    tags: p.tags,
    features: p.features,
    width_in: p.width_in,
    color: p.color,
    is_commercial: p.is_commercial,
    lowest_price_cents: p.lowest_price_cents,
    images: pickProductImages(p),
    // Canonical Supabase-mirrored set (item 156 remainder): the inline entity
    // cards resolve `images_supabase[0] ?? images[0]` so they render the
    // fridge.com-hosted image when one exists (Rule 4), matching the PDP.
    // Fronted to the /product-images/… path here so the array never carries a
    // raw storage host into a payload (FRIDGE-285); `images` above already
    // leads with the same mirrored set.
    // FRIDGE-1666: stored copies of Amazon images never ride it. Once proxied,
    // and beside an `images` already resolved, a copy's source can no longer
    // be told, so they leave here, while the raw row is in hand.
    images_supabase: (() => {
      if (!Array.isArray(p.images_supabase) || p.images_supabase.length === 0) return undefined
      const amazonImages = amazonOriginImages(p)
      const kept = p.images_supabase.filter((u: string) => !amazonImages.has(u.trim())).map((u: string) => getProxiedStoragePath(u))
      return kept.length > 0 ? kept : undefined
    })(),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

// FRIDGE-1501 F7: there is deliberately no bestsellers read. Fridge.com has no
// sales data, and the old one (the cheapest active rows, then a per-type price
// floor that removed every one of them) returned nothing on every page. Rank by
// a signal that exists: getPopularProducts, getTopRated, getTrendingInCollection.

/**
 * Editor's picks of the season — premium (top-priced) products within an
 * article's collection. Falls back to global premium picks if the in-collection
 * set is too thin (e.g., compact / mini-fridge categories where >$1000 is rare).
 */
export async function getEditorsSeasonPicks(
  collectionType: string,
  limit: number = 8
): Promise<RecommendedProduct[]> {
  // FRIDGE-1241: both pools are the same for every caller with the same type
  // and limit (3.3% of all database statement time from 10/08/2026 to
  // 10/17/2026, uncached), so each is one shared read (lib/shared-catalog-read.ts).
  // A failed read is null here, exactly as an error was before.
  const fetchScoped = (scoped: boolean): Promise<any[] | null> =>
    readSharedCatalog('editors-picks-v1', JSON.stringify([scoped ? collectionType : null, limit * 4]), async () => {
      let q = createClient()
        .from('products')
        .select(`
          id,
          brand,
          model,
          title,
          type,
          images,
          images_supabase,
          canonical_url,
          capacity_cuft,
          finish,
          specs_json,
          lowest_price_cents,
          lowest_price_ever_cents
        `)
        .eq('status', 'active')
        .not('lowest_price_cents', 'is', null)
        .gt('lowest_price_cents', 0)
        .order('lowest_price_cents', { ascending: false })

      if (scoped) q = q.eq('type', collectionType)
      const { data, error } = await applyApprovedFilters(q).limit(limit * 4)
      if (error || !data) throw new Error(`editor's picks read failed: ${error?.message ?? 'no data'}`)
      return data as any[]
    }).catch(() => null)

  // Try in-collection first, fall back to global if we don't have enough
  let rawProducts = await fetchScoped(true)
  if (!rawProducts || rawProducts.length < limit) {
    rawProducts = (await fetchScoped(false)) || []
  }

  if (!rawProducts) return []
  const products = rawProducts.filter((p: any) => !isFilterProductType(p.type || ''))

  const recs = products.slice(0, limit * 2).map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: (p.lowest_price_ever_cents && p.lowest_price_ever_cents > p.lowest_price_cents)
      ? p.lowest_price_ever_cents / 100
      : undefined,
    // Real-or-Hidden (Rule 3): real values only
    rating: typeof p.specs_json?.rating === 'number' ? p.specs_json.rating : null,
    reviews: typeof p.specs_json?.reviews === 'number' ? p.specs_json.reviews : null,
  }))

  return postProcess(recs, limit)
}

/**
 * Get related collections (customers searching for X also viewed Y)
 */
export async function getRelatedCollections(
  collectionType: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  // Map collection types to related types
  const relatedTypes: Record<string, string[]> = {
    'french_door': ['side_by_side', 'bottom_freezer', 'top_freezer'],
    'side_by_side': ['french_door', 'bottom_freezer'],
    'top_freezer': ['bottom_freezer', 'french_door', 'mini_fridge'],
    'bottom_freezer': ['french_door', 'side_by_side', 'top_freezer'],
    'mini_fridge': ['beverage_center', 'wine_cooler', 'top_freezer'],
    'upright_freezer': ['chest_freezer', 'compact_freezer'],
    'chest_freezer': ['upright_freezer', 'compact_freezer'],
    'wine_cooler': ['beverage_center', 'mini_fridge'],
    'beverage_center': ['wine_cooler', 'mini_fridge', 'kegerator'],
    'ice_maker': ['mini_fridge', 'beverage_center'],
  }

  const targetTypes = relatedTypes[collectionType] || ['french_door', 'side_by_side']

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .in('type', targetTypes)
    .eq('status', 'active')

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const q = applySafeProductFilters(base, { constrainTypes: false })
  const { data: products, error } = await q.limit(limit * 2)

  if (error || !products) {
    // Silently handle - related collections are optional
    return []
  }

  const recs = products.map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

/**
 * Get featured products (high-rated, premium products)
 * These would typically be higher commission products
 */
export async function getFeaturedProducts(
  collectionType: string,
  limit: number = 4
): Promise<RecommendedProduct[]> {
  const supabase = createClient()

  const base = supabase
    .from('products')
    .select(`
      id,
      brand,
      model,
      title,
      type,
      images,
      images_supabase,
      canonical_url,
      capacity_cuft,
      finish,
      specs_json,
      lowest_price_cents
    `)
    .eq('type', collectionType)
    .eq('status', 'active')

  // Apply safe-product filters (pdp_eligible + banned-title + banned-brand).
  // We pass constrainTypes:false because the caller already narrows to a
  // specific type via .eq('type', ...) / .in('type', ...) above — that's
  // tighter than the global APPROVED_TYPES list. The pdp_eligible bypass
  // here is what caused the May 9 regression where parts leaked into
  // related / collection / frequently-bought / trending / featured carousels.
  const q = applySafeProductFilters(base, { constrainTypes: false })
  const { data: products, error } = await q.limit(limit * 3)

  if (error || !products) {
    // Silently handle - featured products are optional
    return []
  }

  // Filter for premium products (high rating, high price)
  const featured = products.filter((p: any) => {
    const rating = p.specs_json?.rating || 0
    const price = p.lowest_price_cents || 0
    return rating >= 4.5 && price >= 100000 // $1000+
  })

  const recs = featured.slice(0, limit).map((p: any) => ({
    id: p.id,
    brand: p.brand,
    model: p.model,
    title: p.title,
    type: p.type,
    images: pickProductImages(p),
    canonical_url: p.canonical_url || '',
    capacity_cuft: p.capacity_cuft,
    finish: p.finish,
    specs_json: p.specs_json,
    price: p.lowest_price_cents ? p.lowest_price_cents / 100 : undefined,
    originalPrice: undefined,
    rating: p.specs_json?.rating,
    reviews: p.specs_json?.reviews,
  }))
  return postProcess(recs, limit)
}

